import React, {useState, useEffect} from 'react';

import {X, Shield, Mail, Plus, Trash2, Settings} from 'lucide-react';

interface AdminSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EmailDomainCategory {
  name: string;
  domains: string[];
}

export function AdminSettings({isOpen, onClose}: AdminSettingsProps) {
  const [activeTab, setActiveTab] = useState<'roles' | 'domains'>('roles');
  const [domainCategories, setDomainCategories] = useState<EmailDomainCategory[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newCategory, setNewCategory] = useState('Other Providers');
  const [editingDomains, setEditingDomains] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load free email domains from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('freeEmailDomainCategories');
    if (stored) {
      try {
        setDomainCategories(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored domains:', e);
      }
    } else {
      // Load default list
      import('../../constants/freeEmailDomains').then((module) => {
        setDomainCategories(module.FREE_EMAIL_DOMAIN_CATEGORIES);
        localStorage.setItem('freeEmailDomainCategories', JSON.stringify(module.FREE_EMAIL_DOMAIN_CATEGORIES));
      });
    }
  }, []);

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;

    // Basic validation
    if (!domain.includes('.')) {
      alert('Please enter a valid domain (e.g., example.com)');
      return;
    }

    // Check if domain already exists in any category
    const exists = domainCategories.some((cat) => cat.domains.includes(domain));
    if (exists) {
      alert('This domain is already in the list');
      return;
    }

    // Add to selected category
    const updated = domainCategories.map((cat) => {
      if (cat.name === newCategory) {
        return {
          ...cat,
          domains: [...cat.domains, domain].sort(),
        };
      }
      return cat;
    });

    setDomainCategories(updated);
    localStorage.setItem('freeEmailDomainCategories', JSON.stringify(updated));
    setNewDomain('');
  };

  const handleRemoveDomain = (categoryName: string, domain: string) => {
    if (!confirm(`Remove ${domain} from the list?`)) return;

    const updated = domainCategories.map((cat) => {
      if (cat.name === categoryName) {
        return {
          ...cat,
          domains: cat.domains.filter((d) => d !== domain),
        };
      }
      return cat;
    });

    setDomainCategories(updated);
    localStorage.setItem('freeEmailDomainCategories', JSON.stringify(updated));
  };

  const handleResetToDefaults = () => {
    if (!confirm('Reset to default list of free email domains? This will remove any custom domains you added.')) return;

    import('../../constants/freeEmailDomains').then((module) => {
      setDomainCategories(module.FREE_EMAIL_DOMAIN_CATEGORIES);
      localStorage.setItem('freeEmailDomainCategories', JSON.stringify(module.FREE_EMAIL_DOMAIN_CATEGORIES));
    });
  };

  const totalDomains = domainCategories.reduce((sum, cat) => sum + cat.domains.length, 0);

  if (!isOpen) return null;

  // Filter domains based on search
  const filteredCategories = domainCategories
    .map((cat) => ({
      ...cat,
      domains: cat.domains.filter((domain) => domain.toLowerCase().includes(searchQuery.toLowerCase())),
    }))
    .filter((cat) => cat.domains.length > 0 || searchQuery === '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Settings size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Admin Settings</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage system configuration</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('roles')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'roles'
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border-b-2 border-transparent'
              }`}
            >
              <Shield size={16} />
              Roles
            </button>
            <button
              onClick={() => setActiveTab('domains')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'domains'
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border-b-2 border-transparent'
              }`}
            >
              <Mail size={16} />
              Free Email Domains
              {totalDomains > 0 && (
                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-full">{totalDomains}</span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Roles</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Roles are per organisation: every organisation has the built-in roles <strong>Limited user</strong>, <strong>User</strong> and{' '}
                  <strong>Admin</strong>, plus any custom roles it has defined. There is no system-wide list any more.
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Where to manage them</h4>
                <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc pl-5 space-y-1">
                  <li>Open an organisation to see its roles and each member&apos;s role, and to change a member&apos;s role.</li>
                  <li>
                    Account ownership (billing and seat purchasing) is separate from roles. The organisation page shows the account owner and lets a
                    system admin transfer it.
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Free Email Domains Tab */}
          {activeTab === 'domains' && (
            <div className="flex flex-col h-full">
              {/* Toolbar */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Search domains..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 max-w-md px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {totalDomains} total domains in {domainCategories.length} categories
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingDomains(!editingDomains)}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        editingDomains
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                      }`}
                    >
                      {editingDomains ? 'Done Editing' : 'Edit List'}
                    </button>
                    <button
                      onClick={handleResetToDefaults}
                      className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                    >
                      Reset to Defaults
                    </button>
                  </div>
                </div>

                {/* Add new domain */}
                <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add New Domain</label>
                  <div className="flex gap-2">
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {domainCategories.map((cat) => (
                        <option key={cat.name} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                      placeholder="example.com"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleAddDomain}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Add Domain
                    </button>
                  </div>
                </div>
              </div>

              {/* Domains list */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {filteredCategories.map((category) => (
                    <div key={category.name} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                        {category.name}
                        <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                          {category.domains.length}
                        </span>
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {category.domains.map((domain) => (
                          <div
                            key={domain}
                            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Mail size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-900 dark:text-gray-100 font-mono truncate">{domain}</span>
                            </div>
                            {editingDomains && (
                              <button
                                onClick={() => handleRemoveDomain(category.name, domain)}
                                className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                                title="Remove domain"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {filteredCategories.length === 0 && searchQuery && (
                    <div className="text-center py-12">
                      <Mail size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No domains found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>

                <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    <strong>Note:</strong> These domains are based on HubSpot's list of free email providers. Users with these email domains can be
                    filtered on the Users page.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
            <span>
              {totalDomains} free email {totalDomains === 1 ? 'domain' : 'domains'}
            </span>
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
